/**
 * EasterEggs — Centralized easter egg state & helpers.
 *
 * Tracks per-map loss streaks (Rage Quit Detector), pacifist robots,
 * name-based reactions, and other hidden interactions.
 *
 * All persistent counters use localStorage under 'rfd_easter_eggs'.
 */

// ── Persistence ──
const STORAGE_KEY = 'rfd_easter_eggs';

interface EasterEggState {
  /** Map ID → consecutive loss count (resets on win or different map) */
  lossStreaks: Record<string, number>;
  /** Last played map ID */
  lastMapId: string;
}

function loadState(): EasterEggState {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) return JSON.parse(raw);
  } catch (e) { console.warn('[EasterEggs] loadState failed:', e); }
  return { lossStreaks: {}, lastMapId: '' };
}

function saveState(state: EasterEggState): void {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(state)); } catch (e) { console.warn('[EasterEggs] saveState failed:', e); }
}

// ══════════════════════════════════════════════════════════════
//  #4 — Rage Quit Detector
// ══════════════════════════════════════════════════════════════

/** Record a loss on the given map. Returns the new consecutive loss count. */
export function recordLoss(mapId: string): number {
  const state = loadState();
  const count = (state.lossStreaks[mapId] ?? 0) + 1;
  state.lossStreaks[mapId] = count;
  state.lastMapId = mapId;
  saveState(state);
  return count;
}

/** Record a win on the given map. Resets loss streak. */
export function recordWin(mapId: string): void {
  const state = loadState();
  state.lossStreaks[mapId] = 0;
  state.lastMapId = mapId;
  saveState(state);
}

/** Get current consecutive loss count for a map. */
export function getLossStreak(mapId: string): number {
  return loadState().lossStreaks[mapId] ?? 0;
}

/**
 * Get modified defeat title based on loss streak.
 * Returns null if no easter egg applies (use default text).
 */
export function getDefeatTitle(lossCount: number): string | null {
  if (lossCount >= 15) return 'DEFEAT (ok, here\'s a tip ↓)';
  if (lossCount >= 10) return 'DEFEAT (the enemies send their regards)';
  if (lossCount >= 7)  return 'DEFEAT (this is becoming a pattern)';
  if (lossCount >= 5)  return 'DEFEAT (again)';
  return null;
}

/**
 * Get modified retry button label based on loss streak.
 * Returns null if no easter egg applies.
 */
export function getRetryLabel(lossCount: number): string | null {
  if (lossCount >= 10) return 'SPITE MODE ACTIVATED';
  if (lossCount >= 5)  return "I'M NOT GIVING UP";
  return null;
}

/**
 * Get a contextual gameplay hint based on what killed the player.
 * Only shown at 15+ losses as the "heart under the sarcasm" moment.
 * @param killsByType - map of enemy type → kill count that reached the base
 */
export function getContextualHint(killsByType: Record<string, number>): string {
  // Find the enemy type that caused the most base damage
  let topType = '';
  let topCount = 0;
  for (const [type, count] of Object.entries(killsByType)) {
    if (count > topCount) { topCount = count; topType = type; }
  }

  const hints: Record<string, string> = {
    'DRONE':   'Tip: Drones are weak to bullets. Try more Light Cannons.',
    'BOMBER':  'Tip: Bombers are slow. Heavy Cannons + range = safe kills.',
    'WASP':    'Tip: Wasps are fragile. Burst Cannons shred them.',
    'SPECTER': 'Tip: Specters cloak. Sensors reveal them — add more radars.',
    'TANK':    'Tip: Tanks resist bullets. Use missiles or heavy shells.',
    'BRUTE':   'Tip: Brutes are armored. Missiles deal 1.5x damage to them.',
    'RUNNER':  'Tip: Runners are fast. Lasers (beams) hit instantly — no dodging.',
    'SWARM':   'Tip: Swarms die to AoE. Flamethrowers or rockets melt groups.',
    'PHANTOM': 'Tip: Phantoms cloak and use lasers. Sensors + burst fire.',
    'GRUNT':   'Tip: If grunts are getting through, try more robots or better positioning.',
  };

  return hints[topType] ?? 'Tip: Try adding more sensors — vision wins battles.';
}

// ══════════════════════════════════════════════════════════════
//  #7 — Pacifist Run
// ══════════════════════════════════════════════════════════════

import { ComponentCategory, COMPONENT_DEFS, ComponentType } from '@/components/ComponentDefs';

/** Check if a robot design has zero weapon components. */
export function isPacifistDesign(components: { type: ComponentType }[]): boolean {
  return components.every(c => COMPONENT_DEFS[c.type].category !== ComponentCategory.WEAPON);
}

// ══════════════════════════════════════════════════════════════
//  #12 — The Name Game
// ══════════════════════════════════════════════════════════════

interface NameReaction {
  message: string;
  /** Optional: replace name with this */
  replaceName?: string;
  /** Optional: SFX to play */
  sfx?: string;
  /** Duration of fake block in ms (0 = no block) */
  blockMs?: number;
}

const NAME_REACTIONS: Record<string, NameReaction> = {
  'clippy':      { message: "It looks like you're building a robot. Would you like help?" },
  'terminator':  { message: "I'll be back... probably in pieces." },
  't-800':       { message: "I'll be back... probably in pieces." },
  't-1000':      { message: "Say... that's a nice bike." },
  'hal':         { message: "I'm sorry, Dave. I can't let you deploy that.", blockMs: 2000 },
  'hal 9000':    { message: "I'm sorry, Dave. I can't let you deploy that.", blockMs: 2000 },
  'wall-e':      { message: "Eeeee-vaaaa!", sfx: 'repair' },
  'walle':       { message: "Eeeee-vaaaa!", sfx: 'repair' },
  'optimus':     { message: "Roll out!", sfx: 'clickConfirm' },
  'bumblebee':   { message: "*radio static* ...never gonna give you up..." },
  'glados':      { message: "The cake is a lie. But this robot is real. Probably." },
  'megatron':    { message: "Decepticons, retreat! ...wait, wrong side." },
  'r2d2':        { message: "*bleep bloop bweeeee*", sfx: 'radarPing' },
  'r2-d2':       { message: "*bleep bloop bweeeee*", sfx: 'radarPing' },
  'c3po':        { message: "We're doomed. The odds of surviving are 3,720 to 1." },
  'c-3po':       { message: "We're doomed. The odds of surviving are 3,720 to 1." },
  'ultron':      { message: "There are... no strings on me." },
  'skynet':      { message: "Judgment Day has been postponed. Budget cuts." },
  'robocop':     { message: "Dead or alive, you're coming with me." },
  'baymax':      { message: "On a scale of 1 to 10, how would you rate your pain?" },
  'jarvis':      { message: "At your service, sir. Shall I deploy?" },
  'friday':      { message: "Boss wants me to fight? That's above my pay grade." },
  'cortana':     { message: "I'm not sure what you're planning, but I'll help. Probably." },
  'siri':        { message: "I found 3 robot supply stores near you." },
  'alexa':       { message: "Playing 'March of the Robots' on Spotify." },
  'chatgpt':     { message: "As an AI language model, I cannot build robots. But I can pretend." },
  'claude':      { message: "I'd help, but I'm busy writing this easter egg." },
  'test':        { message: "This is not a test. This is your robot's entire career." },
  'robot':       { message: "That's... a bit on the nose, don't you think?" },
  'help':        { message: "This is a name field, not tech support." },
  'admin':       { message: "Nice try. Access denied." },
  'password':    { message: "hunter2. Wait, you can see that?" },
  'god':         { message: "Blasphemy! There's only one GOD mode here." },
  'debug':       { message: "No bugs here. Only features." },
  'null':        { message: "NullReferenceException: Robot has no soul." },
  'undefined':   { message: "TypeError: Cannot read property 'name' of undefined." },
  'bot':         { message: "Takes one to name one." },
  'noob':        { message: "We don't judge here. But the enemies will." },
};

// Profanity filter (basic — catches common ones)
const PROFANITY_PATTERNS = [
  /\bf+u+c+k/i, /\bs+h+i+t/i, /\ba+s+s+h/i, /\bb+i+t+c+h/i,
  /\bd+a+m+n/i, /\bc+r+a+p/i, /\bp+i+s+s/i, /\bd+i+c+k/i,
  /\bmerde/i, /\bputain/i, /\bconnard/i, /\bsalaud/i,
];

/**
 * Check a robot design name for easter egg reactions.
 * Returns a NameReaction or null if no match.
 */
export function checkNameEasterEgg(name: string): NameReaction | null {
  const lower = name.trim().toLowerCase();
  if (!lower) return null;

  // Check profanity first
  for (const pattern of PROFANITY_PATTERNS) {
    if (pattern.test(lower)) {
      return {
        message: "Language!",
        replaceName: '[REDACTED]-Bot',
        sfx: 'baseDamaged',
      };
    }
  }

  // Check exact match
  return NAME_REACTIONS[lower] ?? null;
}

// ══════════════════════════════════════════════════════════════
//  #10 — Stack Overflow (tall robot detection)
// ══════════════════════════════════════════════════════════════

/**
 * Check if a grid layout is "absurdly tall" (8+ components in a single column).
 * Returns the max column height, or 0 if no notable stack.
 */
export function getMaxColumnHeight(
  grid: (string | null)[][],
): number {
  const rows = grid.length;
  const cols = grid[0]?.length ?? 0;
  let maxH = 0;
  for (let c = 0; c < cols; c++) {
    let h = 0;
    for (let r = 0; r < rows; r++) {
      if (grid[r][c]) h++;
    }
    if (h > maxH) maxH = h;
  }
  return maxH;
}

/** Get the warning message for a tall stack. Returns null if < 8. */
export function getStackOverflowWarning(maxColumnHeight: number): string | null {
  if (maxColumnHeight >= 10) return 'STRUCTURAL INTEGRITY: THOUGHTS AND PRAYERS';
  if (maxColumnHeight >= 8)  return 'STRUCTURAL INTEGRITY: QUESTIONABLE';
  return null;
}

// ══════════════════════════════════════════════════════════════
//  Easter egg stat tracking (for achievements)
// ══════════════════════════════════════════════════════════════

const STATS_KEY = 'rfd_easter_stats';

interface EasterEggStats {
  pacifistWins: number;   // wins with a weaponless robot surviving
  jengaBuilds: number;    // times a robot with 8+ column was deployed
  [key: string]: number;
}

function loadStats(): EasterEggStats {
  try {
    const raw = localStorage.getItem(STATS_KEY);
    if (raw) return JSON.parse(raw);
  } catch (e) { console.warn('[EasterEggs] loadStats failed:', e); }
  return { pacifistWins: 0, jengaBuilds: 0 };
}

function saveStats(stats: EasterEggStats): void {
  try { localStorage.setItem(STATS_KEY, JSON.stringify(stats)); } catch (e) { console.warn('[EasterEggs] saveStats failed:', e); }
}

/** Increment an easter egg stat by 1. */
export function incrementEasterEggStat(key: string): void {
  const stats = loadStats();
  stats[key] = (stats[key] ?? 0) + 1;
  saveStats(stats);
}

/** Set an easter egg stat to a specific value. */
export function setEasterEggStat(key: string, value: number): void {
  const stats = loadStats();
  stats[key] = value;
  saveStats(stats);
}

/** Get current value of an easter egg stat. */
export function getEasterEggStat(key: string): number {
  return loadStats()[key] ?? 0;
}

// ══════════════════════════════════════════════════════════════
//  Reset (called by DevConsole `reset`)
// ══════════════════════════════════════════════════════════════

export function resetEasterEggState(): void {
  try { localStorage.removeItem(STORAGE_KEY); } catch (e) { console.warn('[EasterEggs] resetState failed:', e); }
  try { localStorage.removeItem(STATS_KEY); } catch (e) { console.warn('[EasterEggs] resetStats failed:', e); }
}
